import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { formatCad, type AppointmentView } from "@shared/modules/x-atelier";
import { toDate } from "../../../core/lib/format";
import { formatDuration, formatHour, formatTimeRange } from "../lib/dates";
import { dueCents, isInactive } from "../lib/status";
import { QuickActions } from "./quick-actions";
import { StatusBadge, StatusDot } from "./status-badge";
import { BookingChannelBadge, NewClientBadge, PresenceBadge } from "./booking-badges";

/** Durée réelle du créneau, en minutes. */
function slotMinutes(a: AppointmentView): number {
  const start = toDate(a.startAt)?.getTime() ?? 0;
  const end = toDate(a.endAt)?.getTime() ?? start;
  return Math.max(0, Math.round((end - start) / 60_000));
}

interface AppointmentCardProps {
  appointment: AppointmentView;
  /** « compact » : colonne de semaine ; « wide » : liste du jour, une rangée par rendez-vous. */
  layout?: "compact" | "wide";
}

/**
 * Carte d'agenda : le véhicule en gros, le client, le montant dû, la plage
 * horaire et le statut. La couleur du technicien passe par la variable CSS
 * `--tech-color` — seule valeur posée via `style`, la feuille fait le reste.
 * En rangée (vue Jour), les gestes « appeler / texter » sont à côté du lien :
 * un lien dans un lien n'existe pas en HTML, on juxtapose.
 */
export function AppointmentCard({ appointment: a, layout = "compact" }: AppointmentCardProps) {
  const due = dueCents(a);
  const style = a.technicianColor
    ? ({ "--tech-color": a.technicianColor } as CSSProperties)
    : undefined;
  const classes = [
    "agenda-card",
    `agenda-card--${layout}`,
    isInactive(a.status) && "agenda-card--inactive",
  ]
    .filter(Boolean)
    .join(" ");

  const body = (
    <>
      <span className="agenda-card__time">{formatTimeRange(a.startAt, a.endAt)}</span>
      <span className="agenda-card__title">{a.title}</span>
      <span className="agenda-card__client">
        {a.clientName} <BookingChannelBadge channel={a.bookingChannel} compact />
        <PresenceBadge at={a.presenceConfirmedAt} compact />
        <NewClientBadge status={a.clientStatus} compact />
      </span>
      <span className="agenda-card__foot">
        <span className={`agenda-card__due${due === 0 ? " agenda-card__due--paid" : ""}`}>
          {due === 0 && a.totalWithTaxCents > 0 ? "Payé" : formatCad(due)}
        </span>
        <StatusBadge status={a.status} />
      </span>
      {a.technicianName ? <span className="agenda-card__tech">{a.technicianName}</span> : null}
    </>
  );

  // Colonne de semaine : quatre lignes courtes, rien en capitales. Le véhicule
  // se lit en premier, le reste se devine ; le détail est à un tap.
  if (layout === "compact") {
    const paid = due === 0 && a.totalWithTaxCents > 0;
    return (
      <Link
        to={`/travaux/${a.id}`}
        className={classes}
        style={style}
        title={`${a.title} — ${a.clientName}`}
      >
        <span className="agenda-card__row">
          <span className="agenda-card__time">
            {formatHour(a.startAt)} · {formatDuration(slotMinutes(a))}
          </span>
          {a.technicianName ? (
            <span className="agenda-card__tech">{a.technicianName.split(/[\s-]/)[0]}</span>
          ) : null}
        </span>
        <span className="agenda-card__title">{a.title}</span>
        <span className="agenda-card__row">
          <span className="agenda-card__client">
            {a.clientName} <BookingChannelBadge channel={a.bookingChannel} compact />
            <NewClientBadge status={a.clientStatus} compact />
          </span>
          <span className={`agenda-card__due${paid ? " agenda-card__due--paid" : ""}`}>
            {paid ? "Payé" : formatCad(due)}
          </span>
        </span>
        <StatusDot status={a.status} />
      </Link>
    );
  }
  return (
    <article className={classes} style={style}>
      <Link to={`/travaux/${a.id}`} className="agenda-card__link">
        {body}
      </Link>
      <QuickActions phone={a.clientPhone} size="sm" />
    </article>
  );
}
